import { CollectionViewer } from '@angular/cdk/collections';
import { KalDate } from '@kalidea/kaligraphi';
import { Store } from '@ngxs/store';
import gql from 'graphql-tag';
import isEqual from 'lodash-es/isEqual';
import { Observable, Subscription } from 'rxjs';
import { finalize, map, takeWhile, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { ListingDatasource } from 'src/app/_shared/components/listing/listing.datasource';
import {
    KalPaginatorComponent,
    KalTableDatasourceParameters,
    KalTablePageEvent
} from 'src/app/_shared/modules/kal-table/kal-table.module';
import { UserState } from 'src/app/_user/store/user/user.state';
import { ActivitySalesState } from 'src/app/board/activity/stores/activity-sales/activity-sales.state';
import { rangeToFilters } from 'src/app/utils/date.utils';

import {
    CriteresFactureModelInput,
    FactureHistoriqueModel,
    MutationGetListeFacturesPourHistoriqueArgs,
} from 'src/graphql/generated-types';
import { environment } from 'src/environments/environment';

export class ActivityInvoicesFilters {
    IdBenef?: string;
    IdSite?: string;
    IdFournisseur?: string;
    IsFactureFournisseur?: boolean;
    IsFactureClient?: boolean;
    DateFacture?: { startDate: KalDate, endDate: KalDate };
    RechercheString?: string;
    TypesFactures?: string[];
    TypeFacture = 'C'; // Par défaut, facture client
    StatutsFactures?: string[];
    IsSupprimer: boolean;
}

type SharedInvoicesListParams = {
    isFree: boolean;
};

export class SharedInvoicesListDatasource extends ListingDatasource<FactureHistoriqueModel> {

    private subscription = Subscription.EMPTY;
    private lastFilters: any;
    private lastPage: KalTablePageEvent = {} as KalTablePageEvent;

    constructor(config: KalTableDatasourceParameters,
                private readonly graphqlService: GraphqlService,
                private readonly store: Store,
                private readonly params: SharedInvoicesListParams) {
        super(config);

        this.subscription = this.changes$
            .pipe(
                takeWhile(() => !this.isClosed()),
                tap(c => this.fetchRows(c)),
            ).subscribe();
    }

    connect(collectionViewer: CollectionViewer): Observable<FactureHistoriqueModel[] | ReadonlyArray<FactureHistoriqueModel>> {
        return this.datasourceRows.asObservable();
    }

    disconnect(): void {
        this.subscription.unsubscribe();
    }

    reset(config: KalTableDatasourceParameters = {}, options = {forceRefresh: false}): void {
        this.fetchRows(config, options);
    }

    refresh(options: { forceRefresh: boolean } = {forceRefresh: false}): void {
        this.fetchRows(this.parameters, options);
    }

    private extractFilters(filters: ActivityInvoicesFilters): CriteresFactureModelInput {
        if (filters) {
            const StatutsFactures = filters?.StatutsFactures || [];
            const {start, end, operator} = rangeToFilters(filters.DateFacture);
            const formattedFilters: CriteresFactureModelInput = {
                IdBenef: +filters.IdBenef || null,
                IdFournisseur: +filters.IdFournisseur || null,
                IsFactureClient: filters.IsFactureClient || null,
                IsFactureFournisseur: filters.IsFactureFournisseur || null,
                TypeFacture: filters.TypeFacture || null,
                RechercheString: filters.RechercheString || null,
                DateInferieurAux: start,
                DateSuperieurAux: end,
                SelectedComparaison: operator,
                IsSupprimerWithStatutsReglement: !filters.IsSupprimer ? null : true,
                IsSupprimer: filters.IsSupprimer ? null : false,
            };

            if (StatutsFactures.length > 0) {
                formattedFilters.ListeStatuts = {
                    string: filters.StatutsFactures
                };
            }

            ['IsFacture', 'IsAvoir', 'IsAcompte'].forEach(e => {
                formattedFilters[e] = (filters?.TypesFactures || []).some(type => type === e) || null;
            });

            return formattedFilters;
        }
        return null;
    }

    private buildMutationVariables({filters}: KalTableDatasourceParameters, page: KalTablePageEvent): MutationGetListeFacturesPourHistoriqueArgs {

        const isMultiDelegation = this.store.selectSnapshot(UserState.hasMultiDelegationsModule);
        const isSeparatedAccounting = this.store.selectSnapshot(UserState.hasSeparatedAccountingModule);
        const currentCe = this.store.selectSnapshot(UserState.userContext).CurrentCE;

        const Skip = page?.number * page?.size || 0;
        const Take = page?.size || KalPaginatorComponent.defaultPageSize;

        return {
            criteres: {
                IdActivite: this.store.selectSnapshot(ActivitySalesState.currentActivityId) || null,
                CodeVersionLogiciel: environment.constants.codeVersionLogiciel,
                ListIdCommission: { decimal: this.store.selectSnapshot(UserState.commissionsRightsList).map(c => c.IdCommission) },
                IsFactureHide: false,
                IsLibre: this.params.isFree,
                IdDelegOuDelegGlobale: isMultiDelegation ? currentCe : null,
                IdCE: isMultiDelegation ? null : currentCe,
                IsComptaSeparee: isMultiDelegation && isSeparatedAccounting,
                OrderByNumeroFactureDesc: true,
                Skip,
                Take,
                ...this.extractFilters(filters)
            }
        };
    }

    private fetchRows(config: KalTableDatasourceParameters = {}, options: { forceRefresh: boolean } = {forceRefresh: false}): void {
        const {sort, filters, page} = config;
        const comparator = this.getComparator(sort);

        // si on a un comparator
        // et que les filtres n'ont pas changés
        // et qu'il y a déjà des données dans le tableau,
        // et qu'il ne faut pas forcer le rafraichissement,
        // => on va réaliser un tri côté front sans appel à l'api
        if (comparator
            && isEqual(this.lastPage, page)
            && isEqual(filters, this.lastFilters)
            && this.rows.length > 0
            && !options.forceRefresh) {
            this.loading.set(true);
            this.setRows(this.rows.sort(comparator));
            this.loading.set(false);
        } else {
            this.loading.set(true);

            this.graphqlService
                .mutateOne<'GetListeFacturesPourHistorique', MutationGetListeFacturesPourHistoriqueArgs>({
                    mutation: gql`
                    mutation GetListeFacturesPourHistorique($contexte: ContextInfosInput,
                        $criteres: CriteresFactureModelInput) {
                        GetListeFacturesPourHistorique(
                            contexte: $contexte
                            criteres: $criteres
                        ) {
                            Count
                            ListeElements {
                                FactureHistoriqueModel {
                                    IdActivite
                                    IdFacture
                                    IdEcriture
                                    DateFacture
                                    TypeFacture
                                    NumFacture
                                    LibelleFacture
                                    MontantFacture
                                    Solde
                                    IdBenef
                                    BenefPrenom
                                    BenefNom
                                    BenefMat
                                    IdFournisseur
                                    FournisseurNom
                                    StatutFacture
                                    RemarquePublique
                                    RemarquePrivee
                                    IsAvoir
                                    IsAcompte
                                    IsFacture
                                    ConcerneCommandeId
                                    IsFactureAnnulationAvoir
                                    IsSupprime
                                    CodeTypeActivite
                                    HaveAcompteSuCommande
                                    NumeroFactureAnnulationAvoirAssociee
                                    ListeAvoirsLies {
                                        string
                                    }
                                }
                            }
                        }
                    }
                `,
                    variables: this.buildMutationVariables(config, page)
                })
                .pipe(
                    takeWhile(() => !this.isClosed()),

                    // get number of elements
                    tap(data => this.nbOfElementsValue = data.Count),

                    // apply filters in front
                    map(data => {
                        const list = data.ListeElements?.FactureHistoriqueModel || [];
                        return comparator !== null ? list.sort(comparator) : list;
                    }),

                    tap(l => this.setRows(l)),
                    finalize(() => this.loading.set(false))
                )
                .subscribe();
        }
        this.lastFilters = filters;
        this.lastPage = page;
    }

}
